import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { type ColumnDef, DataTable } from '~/components/composites/data-table'
import { Badge } from '~/components/ui/badge'
import { Button } from '~/components/ui/button'
import { Input } from '~/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '~/components/ui/select'
import { useDebounce } from '~/hooks/use-debounce'
import { useQuery } from '~/hooks/use-query'
import { useAppStore } from '~/stores/app'
import { DEFAULT_PAGE_SIZE, DialogId, type ListResponse } from '~/types/common'
import type { NgWord } from '~/types/ng-word'
import { QueryKey } from '~/types/query-key'

type NgWordsListProps = {
  onViewDetail: (item: NgWord) => void
}

export function NgWordsList({ onViewDetail }: NgWordsListProps) {
  const { t } = useTranslation()
  const openDialog = useAppStore((state) => state.openDialog)

  const [search, setSearch] = useState('')
  const [activeFilter, setActiveFilter] = useState<string>('all')
  const [page, setPage] = useState(1)
  const debouncedSearch = useDebounce(search)

  const queries: Record<string, unknown> = {
    page,
    limit: DEFAULT_PAGE_SIZE,
  }
  if (debouncedSearch) queries.search = debouncedSearch
  if (activeFilter !== 'all') queries.isActive = activeFilter === 'true'

  const { data, isLoading } = useQuery<ListResponse<NgWord>>({
    queryKey: QueryKey.NG_WORDS,
    queries,
  })

  const handleRowClick = (item: NgWord) => {
    onViewDetail(item)
    openDialog(DialogId.DETAIL_NG_WORD)
  }

  const columns: ColumnDef<NgWord>[] = [
    {
      key: 'word',
      header: t('ngWords.word'),
      render: (item) => <span className="font-medium">{item.word}</span>,
    },
    {
      key: 'status',
      header: t('common.status'),
      render: (item) => (
        <Badge variant={item.isActive ? 'success' : 'secondary'}>
          {item.isActive ? t('common.active') : t('common.inactive')}
        </Badge>
      ),
      className: 'w-[100px]',
    },
    {
      key: 'createdAt',
      header: t('common.createdAt'),
      render: (item) => new Date(item.createdAt).toLocaleDateString(),
      className: 'w-[150px]',
    },
  ]

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        <Input
          id="ng-words-search"
          placeholder={t('common.search')}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value)
            setPage(1)
          }}
          className="max-w-sm"
        />
        <Select
          value={activeFilter}
          onValueChange={(value) => {
            setActiveFilter(value)
            setPage(1)
          }}
        >
          <SelectTrigger className="w-[150px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('common.all')}</SelectItem>
            <SelectItem value="true">{t('common.active')}</SelectItem>
            <SelectItem value="false">{t('common.inactive')}</SelectItem>
          </SelectContent>
        </Select>
        <Button id="ng-words-add-button" className="ml-auto" onClick={() => openDialog(DialogId.CREATE_NG_WORD)}>
          {t('ngWords.addNew')}
        </Button>
      </div>
      <DataTable
        columns={columns}
        data={data?.items ?? []}
        loading={isLoading}
        pagination={data?.pagination}
        onPageChange={setPage}
        onRowClick={handleRowClick}
      />
    </div>
  )
}
